import React, { useState, useEffect } from 'react';
import { FileText, Wand2, Copy, Check, Moon, Sun } from 'lucide-react';
import mixpanel from './mixpanel';

export default function TextKorrekturApp() {
  const [inputText, setInputText] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [options, setOptions] = useState({
    rechtschreibung: true,
    grammatik: true,
    zeichensetzung: true
  });

  // Track Page View beim ersten Laden
  useEffect(() => {
    mixpanel.track('Page Viewed', {
      page: 'Korrektur.io Home'
    });
  }, []);

  // Dark Mode beim Start laden
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'true') {
      setDarkMode(true);
    }
  }, []);

  // Dark Mode speichern und tracken
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const handleOptionChange = (option) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));

    // Track Option Toggle
    mixpanel.track('Correction Option Toggled', {
      option: option,
      enabled: !options[option]
    });
  };

  const handleCorrect = async () => {
    if (!inputText.trim()) {
      alert('Bitte gib einen Text ein, der korrigiert werden soll.');
      return;
    }

    const selectedOptions = [];
    if (options.rechtschreibung) selectedOptions.push('Rechtschreibung');
    if (options.grammatik) selectedOptions.push('Grammatik');
    if (options.zeichensetzung) selectedOptions.push('Zeichensetzung');

    if (selectedOptions.length === 0) {
      alert('Bitte wähle mindestens eine Korrektur-Option aus.');
      return;
    }

    setLoading(true);
    setCorrectedText('');

    // Track Correction Start
    const startTime = Date.now();
    mixpanel.track('Correction Started', {
      text_length: inputText.length,
      options: selectedOptions,
      dark_mode: darkMode
    });

    try {
      console.log('🔍 Sende Request an:', '/.netlify/functions/correct');
      console.log('📦 Mit Daten:', { text: inputText, options: selectedOptions });

      const response = await fetch('/.netlify/functions/correct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          options: selectedOptions
        })
      });

      console.log('📊 Response Status:', response.status);
      console.log('📋 Response Headers:', [...response.headers.entries()]);
      
      // Lies die Response als Text zuerst
      const responseText = await response.text();
      console.log('📄 Response Text (first 500 chars):', responseText.substring(0, 500));

      // Versuche JSON zu parsen
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('✅ Parsed JSON:', data);
      } catch (e) {
        console.error('❌ JSON Parse Error:', e);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP Error ${response.status}`);
      }

      if (!data.correctedText) {
        throw new Error('No correctedText in response');
      }

      const duration = Date.now() - startTime;
      setCorrectedText(data.correctedText);

      // Track Successful Correction
      mixpanel.track('Correction Completed', {
        text_length: inputText.length,
        corrected_length: data.correctedText.length,
        options: selectedOptions,
        duration_ms: duration,
        success: true
      });

    } catch (error) {
      console.error('❌ Vollständiger Fehler:', error);
      console.error('❌ Error Stack:', error.stack);
      setCorrectedText(`Fehler: ${error.message}`);

      // Track Error
      mixpanel.track('Correction Failed', {
        text_length: inputText.length,
        options: selectedOptions,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(correctedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    // Track Copy Action
    mixpanel.track('Text Copied', {
      text_length: correctedText.length
    });
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);

    // Track Dark Mode Toggle
    mixpanel.track('Dark Mode Toggled', {
      enabled: newMode
    });
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    } p-6`}>
      <div className="max-w-5xl mx-auto">
        {/* Header mit Dark Mode Toggle */}
        <div className="flex justify-end mb-4">
          <button
            onClick={toggleDarkMode}
            className={`p-3 rounded-xl transition-all ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-yellow-400'
                : 'bg-white hover:bg-gray-100 text-gray-700'
            } shadow-lg hover:shadow-xl`}
            aria-label="Dark Mode umschalten"
          >
            {darkMode ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Titel */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <FileText className={`w-10 h-10 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h1 className={`text-4xl font-bold bg-gradient-to-r ${
              darkMode 
                ? 'from-blue-400 to-purple-400' 
                : 'from-blue-600 to-purple-600'
            } bg-clip-text text-transparent`}>
              Korrektur.io
            </h1>
          </div>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Textkorrektur mit Hilfe von künstlicher Intelligenz
          </p>
        </div>

        {/* Hauptbereich */}
        <div className={`rounded-2xl shadow-xl p-8 mb-6 transition-colors ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="mb-6">
            <label className={`block text-sm font-semibold mb-3 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Korrektur-Optionen
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={options.rechtschreibung}
                  onChange={() => handleOptionChange('rechtschreibung')}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className={`transition-colors ${
                  darkMode 
                    ? 'text-gray-300 group-hover:text-blue-400' 
                    : 'text-gray-700 group-hover:text-blue-600'
                }`}>
                  Rechtschreibung
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={options.grammatik}
                  onChange={() => handleOptionChange('grammatik')}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className={`transition-colors ${
                  darkMode 
                    ? 'text-gray-300 group-hover:text-blue-400' 
                    : 'text-gray-700 group-hover:text-blue-600'
                }`}>
                  Grammatik
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={options.zeichensetzung}
                  onChange={() => handleOptionChange('zeichensetzung')}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className={`transition-colors ${
                  darkMode 
                    ? 'text-gray-300 group-hover:text-blue-400' 
                    : 'text-gray-700 group-hover:text-blue-600'
                }`}>
                  Zeichensetzung
                </span>
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-semibold mb-2 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Dein Text
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Gib hier deinen Text ein, der korrigiert werden soll..."
                className={`w-full h-64 p-4 border-2 rounded-xl focus:outline-none resize-none transition-colors ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
              />
              <div className={`text-right text-sm mt-1 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {inputText.length} Zeichen
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`block text-sm font-semibold ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Korrigierter Text
                </label>
                {correctedText && (
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-1 text-sm transition-colors ${
                      darkMode
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Kopiert!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Kopieren
                      </>
                    )}
                  </button>
                )}
              </div>
              <textarea
                value={correctedText}
                readOnly
                placeholder="Die Korrektur erscheint hier..."
                className={`w-full h-64 p-4 border-2 rounded-xl resize-none ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>
          </div>

          <button
            onClick={handleCorrect}
            disabled={loading}
            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Korrigiere...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Text korrigieren
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className={`text-center text-sm ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <p>Powered by Claude AI • Deine Texte werden sicher verarbeitet</p>
        </div>
      </div>
    </div>
  );
}